import { Link, useLocation } from "react-router-dom";
import { cn } from "@jambushrushti/lib/utils";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Calendar,
  Sparkles,
  Newspaper,
  BookOpen,
  Settings,
  LogOut,
  GraduationCap,
  X,
  FileText,
  Search,
  Handshake,
} from "lucide-react";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/scholar/dashboard" },
  { icon: Users, label: "Scholar Directory", href: "/scholar/directory" },
  { icon: FileText, label: "Publications", href: "/scholar/publications" },
  { icon: Handshake, label: "Collaborate", href: "/scholar/collaborate" },
  { icon: Newspaper, label: "Community Feed", href: "/scholar/feed" },
  { icon: Calendar, label: "Events", href: "/scholar/events" },
  { icon: Sparkles, label: "AI Tools", href: "/scholar/ai-tools" },
  { icon: BookOpen, label: "Research Tools", href: "/research" },
  { icon: Settings, label: "Settings", href: "/scholar/settings" },
];

interface ScholarSidebarProps {
  onClose?: () => void;
}

export function ScholarSidebar({ onClose }: ScholarSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <aside className="flex min-h-screen w-72 flex-col bg-primary text-primary-foreground lg:w-64">
      {/* Logo/Brand */}
      <div className="flex items-center justify-between border-b border-primary-foreground/10 p-4 sm:p-6">
        <Link
          to="/scholar/dashboard"
          className="flex items-center gap-3"
          onClick={onClose}
        >
          <div className="bg-orange/20 flex h-10 w-10 items-center justify-center rounded-xl">
            <GraduationCap className="text-orange h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-white">
              Scholar Portal
            </h1>
            <p className="text-xs text-white/60">Jambushrusti</p>
          </div>
        </Link>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* User Info */}
      <div className="border-b border-primary-foreground/10 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="border-orange/30 h-10 w-10 border-2">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-orange/20 text-orange font-semibold">
              {user?.name?.charAt(0).toUpperCase() || "S"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">
              {user?.name || "Demo Scholar"}
            </p>
            <p className="truncate text-xs text-white/60">
              {user?.email || "scholar@demo.com"}
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="border-b border-primary-foreground/10 p-4 lg:hidden">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
          <input
            type="text"
            placeholder="Search..."
            className="focus:ring-orange/50 w-full rounded-lg border-0 bg-white/10 py-2 pl-9 pr-4 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2"
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-auto p-3 sm:p-4">
        {navItems.map(item => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== "/scholar/dashboard" &&
              location.pathname.startsWith(item.href));

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className={cn(
                "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200",
                isActive
                  ? "bg-orange font-medium text-white shadow-lg"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="border-t border-primary-foreground/10 p-4">
        <Button
          variant="ghost"
          className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white"
          onClick={handleLogout}
        >
          <LogOut className="mr-3 h-5 w-5" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
}
